import {zodResolver} from '@hookform/resolvers/zod';
import {Autocomplete, Box, CircularProgress, Stack, TextField, Typography} from '@mui/material';
import React, {Fragment, useState} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {useNavigate} from 'react-router-dom';

import {useQueryClient} from '@tanstack/react-query';
import {QUERY_KEYS} from 'infra/libs/react-query/queryKeys';
import Cookies from 'js-cookie';
import {RefundTaggingEnum} from 'main/analytics/gaTagsEnum';
import {makePostCreateRequestPreview} from 'main/factories/usecases/private/refund/request-preview';
import {makePutCreateRequestPreview} from 'main/factories/usecases/private/refund/request-preview/put-create-request-preview';
import {
	AlertInfo,
	CTAButtonFixed,
	FailToCompleteActionModal,
	MainFooter,
} from 'presentation/components';
import {ControlledRadioGroup} from 'presentation/components/ControlledRadioGroup';
import {Loading} from 'presentation/components/Loading/Loading';
import {HomeEnum, OperationStateTypesEnum} from 'presentation/constants';
import {usePersonalData} from 'presentation/hooks';
import {useAccountInformation} from 'presentation/pages/private/hooks';
import {RequestErrorModal} from 'presentation/pages/private/refund/components';
import {useResetRefundFlow} from 'presentation/pages/private/refund/hooks/useResetRefundFlow';
import {translations} from 'presentation/translations-new';
import {formatText, getUserInfoFromToken} from 'presentation/utils';
import {useGetBeneficiaries} from '../../../Request/hooks/useGetBeneficiaries';
import {useRequestPreviewDataStore} from '../../store/useRequestPreviewDataStore';
import {useRequestPreviewFlowStore} from '../../store/useRequestPreviewFlowStore';
import {DESCRIPTION_MAX_LENGTH, radioOpts} from './constants';
import {FormSchema, FormSchemaValues} from './schema';
import {CustomCTAButtonFixedAction, StyledFooterContainer, StyledForm, Wrapper} from './styles';
import {RefundFormProps} from './types';

const {
	modalityLabel,
	beneficiaryLabel,
	beneficiaryPlaceholder,
	descriptionLabel,
	descriptionHelperText,
	descriptionPlaceholder,
	alertInfo,
	goToAttachments,
} = translations['pt-br'].privateFlow.refund.previewServiceType.refundForm;
const {
	description: descriptionText,
	headerTitle,
	title,
} = translations['pt-br'].privateFlow.refund.previewServiceType.errorModal;
const {loading, notFound, tryAgain, goToHome, requiredField} = translations['pt-br'].commons;
const {responsibleWithotuBenefitsAlert} = translations['pt-br'].privateFlow.refund;

export function ServiceTypeForm({handleNextStep}: RefundFormProps) {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const requestPreviewState = useRequestPreviewDataStore(state => state.data);
	const setRequestPreviewDataState = useRequestPreviewDataStore(state => state.setData);
	const preselectedModality = useRequestPreviewFlowStore(state => state.preselectedModality);
	const [operationState, setOperationState] = useState(OperationStateTypesEnum.NONE);
	const {currentEmail, currentPhone} = usePersonalData();
	const {
		isResponsibleWithoutBenefits,
		beneficiary: {nuContratoC},
	} = useAccountInformation();
	const {loadingList, options, open, handleClose, handleOpen, fetchError, setFetchError} =
		useGetBeneficiaries();
	const {resetRefundFiles} = useResetRefundFlow();
	const userCode = getUserInfoFromToken().cdUsuario;

	const {
		control,
		formState: {errors},
		watch,
		handleSubmit,
	} = useForm<FormSchemaValues>({
		resolver: zodResolver(FormSchema),
		defaultValues: {
			beneficiary: requestPreviewState.beneficiary,
			modality: preselectedModality != null ? preselectedModality : requestPreviewState.modality,
			description: requestPreviewState.description,
		},
	});

	const descriptionValue = watch('description');
	const descriptionLength = descriptionValue.length;

	const IS_ERROR = operationState === OperationStateTypesEnum.ERROR;
	const IS_LOADING = operationState === OperationStateTypesEnum.SUBMITTING;

	const beneficiaryHelperText = errors.beneficiary ? requiredField : null;

	const handleGoToHome = () => {
		navigate(HomeEnum.HOME_PAGE);
	};

	const handleSubmitRequestPreview = async ({
		beneficiary,
		description,
		modality,
	}: FormSchemaValues) => {
		if (IS_LOADING) {
			return;
		}
		try {
			const modalityName = radioOpts.find(item => item.value === modality)?.label;
			setOperationState(OperationStateTypesEnum.SUBMITTING);

			const requestPreviewProtocol = requestPreviewState?.protocol;

			if (requestPreviewProtocol) {
				await makePutCreateRequestPreview().put({
					UserCode: userCode,
					PatientCode: beneficiary.userCode,
					Protocol: requestPreviewProtocol,
					ProtocolCode: requestPreviewState.protocolCode.toString(),
					requestCode: requestPreviewState.requestCode.toString(),
					PhoneNumber: currentPhone,
					Email: currentEmail,
					ModalityCode: modality.toString(),
					OriginRequestCode: '7',
					Preview: 'P',
					EmissionNote: description,
					contractCode: nuContratoC,
				});

				setRequestPreviewDataState({
					...requestPreviewState,
					beneficiary,
					modality,
					description,
				});
			} else {
				const {content} = await makePostCreateRequestPreview().post({
					description,
					email: currentEmail,
					modality: modalityName!,
					modalityCode: modality.toString(),
					patientCode: beneficiary.userCode,
					phoneNumber: currentPhone,
				});
				resetRefundFiles();

				setRequestPreviewDataState({
					...requestPreviewState,
					beneficiary,
					modality,
					description,
					files: [],
					protocol: content.protocol,
					protocolCode: content.protocolCode,
					requestCode: content.requestCode,
					request: content.request,
				});
			}

			handleNextStep();
			queryClient.resetQueries({queryKey: [QUERY_KEYS.REFUND_CONSULT_PREVIEW]});
			Cookies.remove('uploadFilesTerm');
		} catch (error) {
			setOperationState(OperationStateTypesEnum.ERROR);
		}
	};

	return (
		<Fragment>
			<StyledForm onSubmit={handleSubmit(handleSubmitRequestPreview)}>
				<Wrapper>
					<Stack spacing={1}>
						<Typography color="primary" fontWeight={600}>
							{beneficiaryLabel}
						</Typography>
						{isResponsibleWithoutBenefits ? (
							<AlertInfo.Root severity="info">
								<AlertInfo.Description>
									{responsibleWithotuBenefitsAlert}
								</AlertInfo.Description>
							</AlertInfo.Root>
						) : null}
						<Controller
							control={control}
							name="beneficiary"
							render={({field: {onChange, onBlur, value}}) => (
								<Autocomplete
									open={open}
									onOpen={handleOpen}
									onClose={handleClose}
									isOptionEqualToValue={(option, valueOption) =>
										option.userCode === valueOption.userCode
									}
									getOptionLabel={option =>
										option
											? formatText(option.fullSocialName || option.fullName)
											: ''
									}
									options={options ?? []}
									loading={loadingList}
									loadingText={loading}
									noOptionsText={notFound}
									renderInput={params => (
										<TextField
											{...params}
											error={!!errors.beneficiary}
											placeholder={beneficiaryPlaceholder}
											slotProps={{
												input: {
													...params.InputProps,
													endAdornment: (
														<React.Fragment>
															{loadingList ? (
																<CircularProgress
																	color="inherit"
																	size={20}
																/>
															) : null}
															{params.InputProps.endAdornment}
														</React.Fragment>
													),
												},
											}}
											helperText={beneficiaryHelperText}
										/>
									)}
									onChange={(_event, selectedOptions) => {
										onChange(selectedOptions);
										if (selectedOptions) {
											setRequestPreviewDataState({
												...requestPreviewState,
												beneficiary: selectedOptions,
											});
										}
									}}
									onBlur={onBlur}
									value={value}
								/>
							)}
						/>
					</Stack>
					<AlertInfo.Root severity="info">
						<AlertInfo.Description>{alertInfo}</AlertInfo.Description>
					</AlertInfo.Root>
					<Stack spacing={1}>
						<Typography color="primary" fontWeight={600}>
							{modalityLabel}
						</Typography>
						<Box ml={4}>
							<Controller
								name="modality"
								control={control}
								render={({field: {value, onChange}}) => (
									<ControlledRadioGroup
										gap={1.5}
										direction="row"
										value={value}
										onChange={event => {
											onChange(Number(event.target.value));
											setRequestPreviewDataState({
												...requestPreviewState,
												modality: Number(event.target.value),
											});
										}}
										radioOpts={radioOpts}
										forceTextPrimaryColor
									/>
								)}
							/>
						</Box>
					</Stack>

					<Stack spacing={1}>
						<Box sx={{display: 'flex', justifyContent: 'space-between'}}>
							<Typography color="primary" fontWeight={600}>
								{descriptionLabel}
							</Typography>
							<Typography
								variant="caption"
								color="text.secondary"
								textAlign="end"
								sx={{
									display: 'inline-block',
									paddingX: '12px',
									marginTop: 'auto',
								}}>
								{descriptionLength}/ 1000
							</Typography>
						</Box>

						<Controller
							name="description"
							control={control}
							render={({field}) => (
								<TextField
									{...field}
									onChange={event => {
										field.onChange(event.target.value);
										setRequestPreviewDataState({
											...requestPreviewState,
											description: event.target.value,
										});
									}}
									multiline
									slotProps={{htmlInput: {maxLength: DESCRIPTION_MAX_LENGTH}}}
									rows={4}
									placeholder={descriptionPlaceholder}
									helperText={descriptionHelperText}
									error={!!errors.description}
								/>
							)}
						/>
					</Stack>
				</Wrapper>

				<Stack direction="row" justifyContent="flex-end">
					<CTAButtonFixed.Root>
						<CustomCTAButtonFixedAction
							data-tag-id={
								RefundTaggingEnum.CLICK_PROSSEGUIR_PARA_ANEXOS_PREVIA_REEMBOLSO
							}
							type="submit"
							variant="contained"
							endIcon={IS_LOADING ? <Loading size={20} color="inherit" /> : null}>
							{goToAttachments}
						</CustomCTAButtonFixedAction>
					</CTAButtonFixed.Root>
				</Stack>
				<FailToCompleteActionModal
					headerTitle={headerTitle}
					description={descriptionText}
					title={title}
					open={IS_ERROR}
					setOpen={() => setOperationState(OperationStateTypesEnum.PENDING)}
					primaryActionText={tryAgain}
					secondaryActionText={goToHome}
					fallbackAction={handleGoToHome}
					handleTryAgain={handleSubmit(handleSubmitRequestPreview)}
				/>
			</StyledForm>

			<RequestErrorModal open={fetchError} setOpen={setFetchError} />

			<StyledFooterContainer>
				<MainFooter />
			</StyledFooterContainer>
		</Fragment>
	);
}
